import { Flex, ListItemOption, Select, type SelectProps } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import DateComponentField from "../DateInputField/DateInputField";
import { NumericInputField } from "../NumericInputField/NumericInputField";
import { DateRangeBoundType } from "@coreapp/utils/DateRuleUtils";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import React from "react";

const dateTypeOptions = [
  DateRangeBoundType.SpecificDate,
  DateRangeBoundType.Yesterday,
  DateRangeBoundType.Today,
  DateRangeBoundType.Tomorrow,
  DateRangeBoundType.LastNDays,
  DateRangeBoundType.LastNWeeks,
  DateRangeBoundType.LastNMonths,
  DateRangeBoundType.LastNYears,
  DateRangeBoundType.NextNDays,
  DateRangeBoundType.NextNWeeks,
  DateRangeBoundType.NextNMonths,
  DateRangeBoundType.NextNYears
];

const typesOfInput: Record<string, React.JSXElementConstructor<any>> = {
  [DateRangeBoundType.SpecificDate]: DateComponentField,
  [DateRangeBoundType.Yesterday]: DateComponentField,
  [DateRangeBoundType.Today]: DateComponentField,
  [DateRangeBoundType.Tomorrow]: DateComponentField,
  [DateRangeBoundType.LastNDays]: NumericInputField,
  [DateRangeBoundType.LastNWeeks]: NumericInputField,
  [DateRangeBoundType.LastNMonths]: NumericInputField,
  [DateRangeBoundType.LastNYears]: NumericInputField,
  [DateRangeBoundType.NextNDays]: NumericInputField,
  [DateRangeBoundType.NextNMonths]: NumericInputField,
  [DateRangeBoundType.NextNWeeks]: NumericInputField,
  [DateRangeBoundType.NextNYears]: NumericInputField,
  null: NumericInputField
};

type DateOption = {
  dateType: DateRangeBoundType | null;
  value: Date | number | null;
};

const numberOptions = new Set([
  DateRangeBoundType.LastNDays,
  DateRangeBoundType.LastNWeeks,
  DateRangeBoundType.LastNMonths,
  DateRangeBoundType.LastNYears,
  DateRangeBoundType.NextNDays,
  DateRangeBoundType.NextNWeeks,
  DateRangeBoundType.NextNMonths,
  DateRangeBoundType.NextNYears
]);

const disabledDateTypes = new Set<DateRangeBoundType | null>([
  DateRangeBoundType.Yesterday,
  DateRangeBoundType.Today,
  DateRangeBoundType.Tomorrow,
  null
]);

type Props = {
  value: {
    from: DateOption;
    to: DateOption;
  };
  fieldDef?: any;
  onChange: (e: any, data: any) => void;
  enumDateFormat?: string[];
  customInput?: React.JSXElementConstructor<any>;
  returnDateObject?: boolean;
  afterLabelElem?: React.ReactNode;
  size?: SelectProps["selectSize"];
  fromStatus?: SelectProps["status"];
  toStatus?: SelectProps["status"];
};

const DateRangeComponentField = ({
  value,
  fieldDef,
  onChange,
  enumDateFormat,
  customInput,
  returnDateObject,
  fromStatus,
  toStatus,
  size = "small",
  ...props
}: Props) => {
  const FromInput = typesOfInput[value?.from?.dateType as any];
  const ToInput = typesOfInput[value?.to?.dateType as any];
  const fromRef = React.useRef<any>(null);
  const toRef = React.useRef<any>(null);

  const fromInputProps =
    FromInput === NumericInputField
      ? { min: 0, getInputRef: fromRef }
      : { enumDateFormat, customInput, returnDateObject };
  const toInputProps =
    ToInput === NumericInputField
      ? { min: 0, getInputRef: toRef }
      : { enumDateFormat, customInput, returnDateObject };

  const shouldBeDisabled = (dateType: DateRangeBoundType | null) => disabledDateTypes.has(dateType);

  const getBoundStatus = (
    boundStatus: SelectProps["status"],
    dateType: DateRangeBoundType | null
  ) => {
    if (boundStatus !== "error")
      return { selectStatus: "default" as const, inputStatus: undefined as any };
    return shouldBeDisabled(dateType)
      ? { selectStatus: "error" as const, inputStatus: undefined as any }
      : { selectStatus: "default" as const, inputStatus: "error" as const };
  };

  const fromBound = getBoundStatus(fromStatus, value?.from?.dateType);
  const toBound = getBoundStatus(toStatus, value?.to?.dateType);

  const getDefaultValue = (dateType: DateRangeBoundType | null, value: Date | number | null) => {
    const now = new Date();
    switch (dateType) {
      case DateRangeBoundType.Yesterday:
        return new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      case DateRangeBoundType.Today:
        return now;
      case DateRangeBoundType.Tomorrow:
        return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      default:
        return value;
    }
  };

  const getDateTypeOptions = (typeName: "from" | "to") => {
    return dateTypeOptions.map(optionValue => {
      const option: ListItemOption = {
        label: appstr(optionValue).toLowerCase(),
        value: optionValue,
        clickHandler: option => {
          if (option.value === value?.[typeName]?.dateType) {
            return;
          }

          const previousValue = value?.[typeName]?.value;
          let nextValue;
          if (numberOptions.has(optionValue)) {
            nextValue = numberOptions.has(value?.[typeName]?.dateType as any) ? previousValue : 1;
          } else {
            nextValue = "";
          }

          const newValue = {
            from: typeName === "from" ? { dateType: option.value, value: nextValue } : value?.from,
            to: typeName === "to" ? { dateType: option.value, value: nextValue } : value?.to
          };
          onChange(null, newValue);
        },
        dataTestId: `ocn-date-range-option-${typeName}-${optionValue.replace("", "-").replace(/\[n\]/g, "n").toLowerCase()}`
      };

      if (optionValue == "tomorrow") {
        option.divider = true;
      }

      return option;
    });
  };

  const selectListProps = FieldSelectListProps({ enableSearch: true });

  return (
    <Flex gap="12px" className="oc-date-range-input-field">
      <Flex
        gap={"8px"}
        flex={"1"}
        alignItems={"flex-end"}
        className="ocn-filter-date-input-wrappers"
      >
        <Select
          inputOnly={false}
          label={appstr("from")}
          require={!!props.afterLabelElem}
          noMargin
          options={getDateTypeOptions("from")}
          listProps={selectListProps}
          value={value?.from?.dateType ?? undefined}
          inputProps={{ wrapperClassName: "oc-flex-1" }}
          listWidth={"fill"}
          selectSize={size}
          optionSize={size}
          status={fromBound.selectStatus}
          popoverProps={{ flipOptions: { mainAxis: false } }}
        />
        <FromInput
          value={getDefaultValue(value?.from?.dateType, value?.from?.value)}
          onChange={(_e: any, data: string) => {
            const newValue = {
              from: { dateType: value?.from?.dateType, value: data },
              to: value?.to
            };
            onChange(null, newValue);
          }}
          fieldDef={fieldDef}
          size={size}
          disabled={shouldBeDisabled(value?.from?.dateType)}
          status={fromBound.inputStatus}
          showMessage={false}
          {...fromInputProps}
        />
      </Flex>

      <Flex
        gap={"8px"}
        flex={"1"}
        alignItems={"flex-end"}
        className="ocn-filter-date-input-wrappers"
      >
        <Select
          inputOnly={false}
          label={appstr("To (optional)")}
          noMargin
          options={getDateTypeOptions("to")}
          listProps={selectListProps}
          value={value?.to?.dateType ?? undefined}
          inputProps={{ wrapperClassName: "oc-flex-1" }}
          listWidth={"fill"}
          selectSize={size}
          optionSize={size}
          status={toBound.selectStatus}
          popoverProps={{ flipOptions: { mainAxis: false } }}
          placeholder={appstr("To (optional)")}
          clearIconSize="xsmall"
          onClear={
            value?.to?.dateType != null
              ? () => {
                  const newValue = {
                    from: value?.from,
                    to: { dateType: null, value: "" }
                  };
                  onChange(null, newValue);
                }
              : undefined
          }
        />
        <ToInput
          value={getDefaultValue(value?.to?.dateType, value?.to?.value)}
          onChange={(_e: any, data: string) => {
            const newValue = {
              from: value?.from,
              to: { dateType: value?.to?.dateType, value: data }
            };
            onChange(null, newValue);
          }}
          size={size}
          fieldDef={fieldDef}
          disabled={shouldBeDisabled(value?.to?.dateType)}
          placeholder={value?.to?.dateType == null ? "--" : ""}
          status={toBound.inputStatus}
          showMessage={false}
          {...toInputProps}
        />
      </Flex>
    </Flex>
  );
};

export default DateRangeComponentField;
